import { useEffect, useState } from "react";
import { admin, AdminReport, formatMoney, ParseReportStatus } from "../../api/client";
import { fmtDate, PageHeader, useFlash } from "./common";
import { MessageInspector } from "./Messages";

const REASON_LABEL: Record<string, string> = {
  wrong_vendor: "Wrong vendor",
  wrong_amount: "Wrong amount",
  wrong_date: "Wrong date",
  wrong_type: "Wrong type",
  other: "Other",
};

export default function Reports() {
  const [status, setStatus] = useState<"" | ParseReportStatus>("open");
  const [reason, setReason] = useState("");
  const [rows, setRows] = useState<AdminReport[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [inspect, setInspect] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const flash = useFlash(setToast);

  const load = () => admin.reports({ status, reason: reason || undefined }).then(setRows).catch((e) => setError(e.message));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, reason]);

  async function resolve(r: AdminReport, next: ParseReportStatus) {
    const note = next === "open" ? null : prompt("Resolution note (optional):") ?? null;
    try {
      await admin.updateReport(r.id, { status: next, resolution_note: note });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Update failed");
    }
  }

  async function reparse(r: AdminReport) {
    try {
      const res = await admin.reparseReport(r.id);
      flash(res.message);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Reparse failed");
    }
  }

  return (
    <div className="page">
      <PageHeader title="Parse reports" sub="Receipts users flagged as parsed incorrectly. Inspect the email, reparse, or resolve." />
      <div className="filter-bar">
        <select value={status} onChange={(e) => setStatus(e.target.value as ParseReportStatus)} aria-label="Status">
          <option value="open">Open</option>
          <option value="resolved">Resolved</option>
          <option value="dismissed">Dismissed</option>
          <option value="">All</option>
        </select>
        <select value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Reason">
          <option value="">Any reason</option>
          {Object.entries(REASON_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      {error && <div className="page-error">{error}</div>}
      {rows.length === 0 ? (
        <div className="page-empty">Nothing to review.</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Reported</th>
              <th>User</th>
              <th>Email</th>
              <th>Current parse</th>
              <th>Problem</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td style={{ whiteSpace: "nowrap" }}>{fmtDate(r.created_at)}</td>
                <td>{r.owner_email}</td>
                <td style={{ maxWidth: 260 }}>
                  <div>{r.reported_subject}</div>
                  <div className="muted">{r.reported_sender}</div>
                </td>
                <td>
                  {r.receipt_id ? (
                    <>
                      {r.receipt_vendor || "—"} · {formatMoney(r.receipt_total_cents, r.receipt_currency)} · {r.receipt_type}
                    </>
                  ) : (
                    <span className="muted">receipt replaced/deleted</span>
                  )}
                </td>
                <td style={{ maxWidth: 260 }}>
                  <strong>{REASON_LABEL[r.reason] ?? r.reason}</strong>
                  {r.details && <div>{r.details}</div>}
                  {r.resolution_note && <div className="muted">Resolution: {r.resolution_note}</div>}
                </td>
                <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                  {r.email_message_id && <button className="btn btn-ghost btn-sm" onClick={() => setInspect(r.email_message_id!)}>Inspect</button>}
                  {r.receipt_id && r.status === "open" && <button className="btn btn-ghost btn-sm" onClick={() => reparse(r)}>Reparse</button>}
                  {r.status === "open" ? (
                    <>
                      <button className="btn btn-ghost btn-sm" onClick={() => resolve(r, "resolved")}>Resolve</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => resolve(r, "dismissed")}>Dismiss</button>
                    </>
                  ) : (
                    <button className="btn btn-ghost btn-sm" onClick={() => resolve(r, "open")}>Reopen</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {inspect && <MessageInspector id={inspect} onClose={() => setInspect(null)} onReparse={load} />}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
